import { SRM } from '@/shared/config/routes';
import type { TimeRange } from '@/shared/lib/utils/date-time/time-range';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ViewType } from '../config/view';
import {
  SRM_CI_HEALTH_RANGE_PARAM,
  SRM_CI_HEALTH_STAGE_PARAM,
  SRM_CI_PARAM,
  SRM_HI_TYPE_NAME_PARAM,
  SRM_SEVERITY_PARAM,
  SRM_SOT_ID_PARAM,
  SRM_TQL_HI_LINK_ID_PARAM,
  SRM_VIEW_PARAM,
} from './url-state';

type CIHealthTarget = {
  ciId?: string;
  tqlHiLinkId?: string;
  hiTypeName?: string;
  sotId?: string;
  severity?: number;
  range?: TimeRange;
};

export type CIHealthNavigationOptions = Pick<
  CIHealthTarget,
  'tqlHiLinkId' | 'hiTypeName' | 'sotId' | 'severity' | 'range'
>;

export const getCIHealthURL = (target: string | CIHealthTarget = {}) => {
  const { ciId, tqlHiLinkId, hiTypeName, sotId, severity, range } =
    typeof target === 'string' ? { ciId: target } : target;
  const hasSelectedHI = Boolean(ciId && tqlHiLinkId && hiTypeName);
  const params = new URLSearchParams({
    [SRM_VIEW_PARAM]: ViewType.CI_HEALTH,
    [SRM_CI_HEALTH_STAGE_PARAM]: hasSelectedHI
      ? 'hi'
      : ciId
        ? 'ci'
        : 'registry',
  });
  if (ciId) params.set(SRM_CI_PARAM, ciId);
  if (tqlHiLinkId) params.set(SRM_TQL_HI_LINK_ID_PARAM, tqlHiLinkId);
  if (hiTypeName) params.set(SRM_HI_TYPE_NAME_PARAM, hiTypeName);
  if (sotId) params.set(SRM_SOT_ID_PARAM, sotId);
  if (severity != null) params.set(SRM_SEVERITY_PARAM, String(severity));
  if (range) params.set(SRM_CI_HEALTH_RANGE_PARAM, JSON.stringify(range));

  return `${SRM}?${params.toString()}`;
};

export const resolveCIHealthURL = (
  ciId?: string,
  options: CIHealthNavigationOptions = {},
) => {
  if (ciId && options.tqlHiLinkId && options.hiTypeName) {
    return getCIHealthURL({ ciId, ...options });
  }

  return getCIHealthURL({
    ciId,
    severity: options.severity,
    range: options.range,
  });
};

export const useNavigateToCIHealth = () => {
  const navigate = useNavigate();

  return useCallback(
    (ciId?: string, options: CIHealthNavigationOptions = {}) =>
      navigate(resolveCIHealthURL(ciId, options)),
    [navigate],
  );
};